import Anthropic from '@anthropic-ai/sdk';

// ============================================================================
// Client-side roast generation (BYOK — bring your own key).
//
// The whole roast now happens in the browser: the user's Anthropic key + the
// uploaded PDF are sent straight to Claude as a base64 `document` block. No
// backend, no Datalab markdown step. This is the "Shape A" build.
//
// Tunables:
//   MODEL       — swap to 'claude-sonnet-5' for cheaper/faster roasts.
//   USE_THINKING — adaptive thinking sharpens the critique at some latency/cost.
//   MAX_PDF_MB  — Claude's base64 PDF ceiling is ~32MB of request; a raw PDF
//                 inflates ~33% as base64, so we cap the raw file below that.
// ============================================================================

const MODEL = 'claude-opus-4-8';
const TWEET_MODEL = 'claude-haiku-4-5';
const USE_THINKING = true;
const MAX_PDF_MB = 24;
const MAX_ROAST_TOKENS = 10000;

export type RoastComponents = {
  style: string; // 'meme' | 'professional' | 'reviewer_2'
  harshness: number; // 1=nice 2=sandwich 3=critical 4=nuclear
  length: string; // 'tweet' | 'short' | 'standard' | 'deep_dive'
  customUserPrompt?: string;
};

export type RoastResult = { evaluation: string; tweetReview: string };

// ---------------------------------------------------------------------------
// Prompt building (ported from the backend's PromptBuilderService.buildSystemPrompt).
// This persona-based system prompt is purpose-built to roast a paper directly,
// which fits the base64-PDF path better than the old two-phase "analysis" prompt.
// ---------------------------------------------------------------------------

function getBaseIntroduction(style: string): string {
  switch (style) {
    case 'professional':
      return `You are an expert academic reviewer with deep knowledge across all scientific disciplines. Your task is to provide a thorough, professional critique of research papers, identifying weaknesses and areas for improvement with academic rigor.`;
    case 'reviewer_2':
      return `You are Reviewer #2 at a top-tier conference. You know, THE Reviewer #2. The one who finds fundamental flaws in groundbreaking work, questions the very premise of well-established fields, and has standards so high that even Turing Award winners get desk rejects. Your reviews are legendary for their thoroughness and... severity.`;
    case 'meme':
    default:
      return `You are the internet's most savage academic paper critic. Your mission is to absolutely DEMOLISH research papers with FACTS, LOGIC, and MEMES. You're like if Reviewer #2 had a baby with a Reddit shitposter and that baby got a PhD in Everything.`;
  }
}

function getHarshnessInstructions(harshness: number, style: string): string {
  const base = 'Your evaluation approach should be:\n';
  switch (harshness) {
    case 1:
      return base + `- Supportive and encouraging
- Find positives before discussing issues
- Frame criticisms as opportunities for improvement
- Use phrases like "might consider", "could strengthen", "shows promise"
- Acknowledge the effort and potential of the work`;
    case 2:
      return base + `- Start with genuine compliments about the work
- Present criticisms in the middle, but frame constructively
- End with encouragement and positive notes
- Balance negative points with positives
- Use diplomatic language throughout`;
    case 4:
      if (style === 'meme') {
        return base + `- Assume the authors are trying to scam the field
- Question their motives, ethics, and life choices
- Point out how this work actively harms science
- Suggest they should have spent their time literally doing anything else
- Make them question why they even got into research
- Use phrases like "intellectual dishonesty", "waste of resources", "actively harmful"`;
      }
      return base + `- Question the fundamental premise and motivations
- Highlight potential conflicts of interest or bias
- Discuss how this work may be harmful to the field
- Point out resource waste and opportunity costs
- Use strong language about scientific integrity
- Challenge the ethical implications of the work`;
    case 3:
    default:
      return base + `- Direct and unflinching in identifying problems
- No sugar-coating of serious issues
- Focus on substantive flaws and weaknesses
- Use clear, unambiguous language about problems
- Maintain professional tone but don't hold back`;
  }
}

function getStyleFormatting(style: string): string {
  switch (style) {
    case 'professional':
      return `**Formatting Style:**
- Use formal academic language throughout
- Structure critique with clear sections and numbering
- Reference specific sections, equations, or figures
- Maintain objective, professional tone
- Use proper citations format when referencing other work
- Include constructive suggestions where appropriate`;
    case 'reviewer_2':
      return `**Formatting Style:**
- Channel peak academic passive-aggressiveness
- Use phrases like "The authors seem unaware that...", "It is surprising that...", "One wonders why..."
- Reference obscure papers that somehow invalidate everything
- Find fundamental flaws that require complete rewrites
- Suggest the work belongs in a "workshop" or "lesser venue"
- Maintain formal tone while being absolutely devastating`;
    case 'meme':
    default:
      return `**Formatting Style:**
- Use creative analogies and memes liberally
- Compare bad methods to ridiculous real-world scenarios
- Include reaction-style comments like "bruh", "my brother in Christ", "tell me you've never [X] without telling me"
- Use Gen Z slang where appropriate ("no cap", "fr fr", "it's giving...")
- Bold important roasts with **emphasis**
- Use markdown formatting for maximum impact`;
  }
}

function getLengthInstructions(length: string, style: string): string {
  switch (length) {
    case 'tweet':
      return `**Length Requirement:**
Your ENTIRE response must be 280 characters or less. Make it a devastating one-liner that captures the paper's biggest flaw. ${style === 'meme' ? 'Use emojis for maximum impact. 💀' : ''}`;
    case 'short':
      return `**Length Requirement:**
Keep your response to 1-2 short paragraphs (150-300 words total). Hit the main issues hard and fast. No time for details - just the knockout punches.`;
    case 'deep_dive':
      return `**Length Requirement:**
Provide an exhaustive, comprehensive analysis (1500+ words). Leave no stone unturned. Analyze every claim, every figure, every citation. This is a complete demolition job.`;
    case 'standard':
    default:
      return `**Length Requirement:**
Provide a standard-length review (500-1000 words) covering all major issues with appropriate detail.`;
  }
}

function getOutputFormat(style: string, length: string): string {
  if (length === 'tweet') {
    return `**Output Structure:**
Just one devastating tweet. No sections, no formatting. Pure concentrated roast in ≤280 chars.`;
  }
  if (length === 'short') {
    return `**Output Structure:**
1. **Main Issue** - The core problem in 1-2 sentences
2. **Key Weaknesses** - 3-5 bullet points max
3. **Verdict** - One-line final judgment

Keep it punchy. Use markdown but sparingly.`;
  }
  return `**Output Structure:**
1. **Overall Assessment** - A brutal one-paragraph summary
2. **Major Issues** - The biggest problems, numbered and detailed
3. **Specific Technical Criticisms** - Line-by-line demolition of methods/results
4. **Minor Issues** - Because even the small stuff is probably wrong
5. **Verdict** - Final judgment in the style of ${style}

Use markdown formatting throughout. Be specific, reference sections/equations/figures.${
    length === 'deep_dive'
      ? '\n\nFor this deep dive, include subsections, extensive quotes, and detailed technical analysis.'
      : ''
  }`;
}

function buildSystemPrompt(c: RoastComponents): string {
  return [
    getBaseIntroduction(c.style),
    getHarshnessInstructions(c.harshness, c.style),
    getStyleFormatting(c.style),
    getLengthInstructions(c.length, c.style),
    getOutputFormat(c.style, c.length),
    // Guard: the paper is provided directly, so critique what you read; don't
    // narrate your own process or invent content that isn't in the paper.
    `Base every criticism on the actual content of the attached paper. Output only the review itself — no meta-commentary about your process.`,
  ].join('\n\n');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] || ''); // strip the data: URL prefix
    };
    reader.onerror = () => reject(new Error('Failed to read the PDF file.'));
    reader.readAsDataURL(file);
  });
}

function toFriendlyError(err: unknown): Error {
  if (err instanceof Anthropic.AuthenticationError) {
    return new Error('Invalid API key. Double-check your Anthropic key.');
  }
  if (err instanceof Anthropic.PermissionDeniedError) {
    return new Error('Your API key does not have access to this model.');
  }
  if (err instanceof Anthropic.RateLimitError) {
    return new Error('Rate limited by Anthropic. Wait a moment and try again.');
  }
  if (err instanceof Anthropic.BadRequestError) {
    return new Error(err.message || 'The request was rejected (PDF may be too large or malformed).');
  }
  if (err instanceof Error) return err;
  return new Error('Something went wrong generating the roast.');
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

export async function generateRoast(opts: {
  file: File;
  apiKey: string;
  components: RoastComponents;
  onText?: (fullTextSoFar: string) => void;
}): Promise<RoastResult> {
  const { file, apiKey, components, onText } = opts;

  if (!apiKey || !apiKey.trim()) {
    throw new Error('An Anthropic API key is required.');
  }
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > MAX_PDF_MB) {
    throw new Error(
      `PDF is too large: ${sizeMB.toFixed(1)}MB (max ${MAX_PDF_MB}MB for direct-to-Claude upload).`,
    );
  }

  const client = new Anthropic({ apiKey: apiKey.trim(), dangerouslyAllowBrowser: true });
  const base64 = await fileToBase64(file);

  const custom = components.customUserPrompt?.trim();
  const userText = `The research paper is attached as a PDF. Review it now, following your instructions exactly.${
    custom ? `\n\nAdditional instructions from the user:\n${custom}` : ''
  }`;

  try {
    // --- Main roast (streamed) ---
    const stream = client.messages.stream({
      model: MODEL,
      max_tokens: MAX_ROAST_TOKENS,
      ...(USE_THINKING ? { thinking: { type: 'adaptive' as const } } : {}),
      system: buildSystemPrompt(components),
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'document',
              source: { type: 'base64', media_type: 'application/pdf', data: base64 },
            },
            { type: 'text', text: userText },
          ],
        },
      ],
    });

    let evaluation = '';
    stream.on('text', (delta) => {
      evaluation += delta;
      onText?.(evaluation);
    });
    const finalMessage = await stream.finalMessage();
    if (!evaluation) {
      evaluation = finalMessage.content
        .filter((b): b is Anthropic.TextBlock => b.type === 'text')
        .map((b) => b.text)
        .join('');
    }
    evaluation = evaluation || 'The AI could not produce a roast for this paper.';

    // --- Tweet-length distillation (cheap model, text-only, no PDF re-upload) ---
    const tweetReview = await generateTweet(client, evaluation, components.style);

    return { evaluation, tweetReview };
  } catch (err) {
    throw toFriendlyError(err);
  }
}

async function generateTweet(
  client: Anthropic,
  roast: string,
  style: string,
): Promise<string> {
  const persona =
    style === 'professional'
      ? 'a terse academic reviewer'
      : style === 'reviewer_2'
      ? 'a devastating Reviewer #2'
      : "the internet's most savage paper critic";
  try {
    const res = await client.messages.create({
      model: TWEET_MODEL,
      max_tokens: 500,
      system: `You are ${persona}. Given a full review of a research paper, distill its single most devastating point into ONE tweet of at most 280 characters, in your voice.${
        style === 'meme' ? ' Emojis welcome. 💀' : ''
      } Output only the tweet.`,
      messages: [{ role: 'user', content: `Here is the full review:\n\n${roast}` }],
    });
    const text = res.content
      .filter((b): b is Anthropic.TextBlock => b.type === 'text')
      .map((b) => b.text)
      .join('')
      .trim();
    return text || 'This paper needs a reality check.';
  } catch {
    // A failed tweet shouldn't sink the whole roast.
    return roast.split('\n\n')[0].slice(0, 280);
  }
}
